import { useState } from "react"
import { cn } from "@/lib/utils"
import { ChevronDownIcon, FileTextIcon, UploadIcon, UsersIcon } from "lucide-react"
import { Badge } from "@/components/ui/badge"

export interface BrainstormingSubmissionItem {
  id: string
  session: string
  sessionUrl?: string | null
  submittedBy: string
  content?: string | null
  fileUrl?: string | null
  fileName?: string | null
  feedbackStatus: string
  newFeedback?: boolean
  feedback?: string | null
  submittedAt?: string | null
  commentCount?: number
  adminCommentCount?: number
}

export interface MeetupUploadItem {
  id: string
  session: string
  submittedBy: string
  uploadType: string
  description?: string | null
  fileUrl?: string | null
  fileName?: string | null
  feedbackStatus: string
  uploadedAt?: string | null
  commentCount?: number
  adminCommentCount?: number
}

export interface InterviewItem {
  id: string
  contact: string
  segment?: string
  date?: string | null
  type: string
  keyInsights?: string
  fullNotes?: string
  commentCount?: number
  adminCommentCount?: number
}

interface CollapsibleSectionProps {
  title: string
  count: number
  notificationCount?: number
  icon: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
}

function CollapsibleSection({ title, count, notificationCount = 0, icon, defaultOpen = false, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="bg-card border border-border rounded-lg overflow-visible relative">
      {notificationCount > 0 && (
        <span className="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center shadow-sm">
          {notificationCount > 9 ? '9+' : notificationCount}
        </span>
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-foreground">{title}</span>
          <Badge variant="secondary" className="text-xs">{count}</Badge>
        </div>
        <ChevronDownIcon className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="border-t border-border overflow-x-auto">
          {children}
        </div>
      )}
    </div>
  )
}

function feedbackBadge(status: string) {
  const variant = status === "good" ? "default"
    : status === "needs_improvement" ? "destructive"
    : status === "reviewed" ? "secondary"
    : "outline"
  const label = status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  return <Badge variant={variant} className="text-xs">{label}</Badge>
}

export interface TeamSubmissionsProps {
  brainstormingSubmissions: BrainstormingSubmissionItem[]
  meetupUploads: MeetupUploadItem[]
  interviews: InterviewItem[]
  className?: string
}

export function TeamSubmissions({ brainstormingSubmissions, meetupUploads, interviews, className }: TeamSubmissionsProps) {
  const totalItems = brainstormingSubmissions.length + meetupUploads.length + interviews.length
  if (totalItems === 0) return null

  // Notification counts: items with NEW admin activity since last viewed
  const bsNotifications = brainstormingSubmissions.filter(bs => bs.newFeedback || (bs.adminCommentCount && bs.adminCommentCount > 0)).length
  const mduNotifications = meetupUploads.filter(mdu => (mdu.adminCommentCount && mdu.adminCommentCount > 0)).length
  const intNotifications = interviews.filter(i => i.adminCommentCount && i.adminCommentCount > 0).length

  return (
    <div className={cn("grid grid-cols-1 lg:grid-cols-3 gap-4", className)}>
      {/* Brainstorming Submissions */}
      <CollapsibleSection
        title="Brainstorming Submissions"
        count={brainstormingSubmissions.length}
        notificationCount={bsNotifications}
        icon={<FileTextIcon className="h-4 w-4 text-muted-foreground" />}
      >
        {brainstormingSubmissions.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">No submissions yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {brainstormingSubmissions.map((bs) => (
              <div key={bs.id} className="px-4 py-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  {bs.sessionUrl ? (
                    <a href={bs.sessionUrl} className="text-sm font-medium text-primary hover:underline truncate">{bs.session}</a>
                  ) : (
                    <span className="text-sm font-medium text-foreground truncate">{bs.session}</span>
                  )}
                  {feedbackBadge(bs.feedbackStatus)}
                </div>
                <p className="text-xs text-muted-foreground">by {bs.submittedBy} &middot; {bs.submittedAt}</p>
                {bs.content && (
                  bs.sessionUrl ? (
                    <a href={bs.sessionUrl} className="text-xs text-primary hover:underline line-clamp-2 block">{bs.content}</a>
                  ) : (
                    <p className="text-xs text-foreground line-clamp-2">{bs.content}</p>
                  )
                )}
                {bs.fileUrl && (
                  <a href={bs.fileUrl} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <FileTextIcon className="h-3 w-3" /> {bs.fileName}
                  </a>
                )}
                {bs.feedback && (
                  <div className="mt-1 p-2 bg-muted/50 rounded text-xs text-foreground">
                    <span className="font-medium">Feedback: </span>
                    {bs.feedback.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                      part.match(/^https?:\/\//) ? (
                        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
                           className="text-primary hover:underline">{part}</a>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Meetup Data Uploads */}
      <CollapsibleSection
        title="Meetup Data Uploads"
        count={meetupUploads.length}
        notificationCount={mduNotifications}
        icon={<UploadIcon className="h-4 w-4 text-muted-foreground" />}
      >
        {meetupUploads.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">No uploads yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {meetupUploads.map((mdu) => (
              <div key={mdu.id} className="px-4 py-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground truncate">{mdu.session}</span>
                  {feedbackBadge(mdu.feedbackStatus)}
                </div>
                <p className="text-xs text-muted-foreground">
                  by {mdu.submittedBy} &middot; {mdu.uploadedAt}
                  <Badge variant="outline" className="ml-2 text-xs">{mdu.uploadType}</Badge>
                </p>
                {mdu.description && (
                  <p className="text-xs text-foreground line-clamp-2">{mdu.description}</p>
                )}
                {mdu.fileUrl && (
                  <a href={mdu.fileUrl} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <FileTextIcon className="h-3 w-3" /> {mdu.fileName}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Interview Data */}
      <CollapsibleSection
        title="Interview Data"
        count={interviews.length}
        notificationCount={intNotifications}
        icon={<UsersIcon className="h-4 w-4 text-muted-foreground" />}
      >
        {interviews.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">No interviews yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {interviews.map((i) => (
              <div key={i.id} className="px-4 py-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground truncate">{i.contact}</span>
                  <Badge variant="secondary" className="text-xs">{i.type === "video-call" ? "Video" : i.type === "phone" ? "Phone" : i.type === "in-person" ? "In Person" : i.type}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {i.segment && <>{i.segment} &middot; </>}{i.date}
                </p>
                {i.keyInsights && (
                  <div className="mt-1">
                    <p className="text-xs font-medium text-muted-foreground">Key Insights</p>
                    <p className="text-xs text-foreground line-clamp-3">{i.keyInsights}</p>
                  </div>
                )}
                {i.fullNotes && (
                  <details className="mt-1">
                    <summary className="text-xs text-primary cursor-pointer hover:underline">Full Notes</summary>
                    <p className="text-xs text-foreground mt-1 whitespace-pre-wrap">{i.fullNotes}</p>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>
    </div>
  )
}
